import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import StartAssessmentDialog from "@/components/assessments/StartAssessmentDialog";
import AssessmentFiller from "@/components/assessments/AssessmentFiller";
import AssessmentList from "@/components/assessments/AssessmentList";
import DuePanel from "@/components/assessments/DuePanel";
import CycleReportDialog from "@/components/assessments/CycleReportDialog";
import { ASSESSMENT_TYPES } from "@/components/assessments/assessmentMeta";
import { ClipboardCheck, Plus, Stethoscope, ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RnAssessmentsPage() {
  const { user } = useAuth();
  const canEdit = ["rn", "admin"].includes((user?.role || "").toLowerCase());
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [items, setItems] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startCtx, setStartCtx] = useState({}); // {clientId, clientName, defaultType}
  const [openId, setOpenId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/assessments`, { params: { status, assessment_type: type === "all" ? undefined : type } });
      setItems(r.data.assessments);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load assessments");
      setItems([]);
    }
  }, [status, type]);

  useEffect(() => { load(); }, [load]);
  const closeFiller = useCallback(() => setOpenId(null), []);
  const reload = () => { load(); setRefreshKey((k) => k + 1); };
  const startFor = (it) => { setStartCtx({ clientId: it.client_id, clientName: it.client_name, defaultType: it.kind === "form_update" ? "supervisory" : it.kind }); setStarting(true); };

  const drafts = (items || []).filter((a) => a.status === "draft").length;
  const completed = (items || []).filter((a) => a.status === "completed").length;

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6" data-testid="rn-assessments-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Stethoscope className="w-6 h-6 text-rose-600" /> RN Assessments</h1>
            <p className="text-sm text-slate-500">Admission, Supervisory Visit and Re-Admission assessments completed in the home on your agency's forms.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setReportOpen(true)} className="gap-2" title="Every client's Plan of Care cycle: updates done, next due, overdue" data-testid="cycle-report-btn">
              <ClipboardList className="w-4 h-4" /> Cycle report
            </Button>
            {canEdit && (
              <Button onClick={() => { setStartCtx({}); setStarting(true); }} className="gap-2 bg-rose-600 hover:bg-rose-700" data-testid="page-new-assessment-btn">
                <Plus className="w-4 h-4" /> Start assessment
              </Button>
            )}
          </div>
        </div>
        {reportOpen && (
          <CycleReportDialog
            canEdit={canEdit}
            onClose={() => { setReportOpen(false); reload(); }}
            onOpenDraft={(id) => { setReportOpen(false); setOpenId(id); }}
            onRevise={(a) => { setReportOpen(false); reload(); setOpenId(a.id); }}
          />
        )}

        <DuePanel canEdit={canEdit} isAdmin={isAdmin} onStart={startFor} refreshKey={refreshKey} />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="stat-drafts">
            <p className="text-xs uppercase tracking-wide text-amber-700">In progress</p>
            <p className="text-2xl font-bold text-amber-900">{drafts}</p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="stat-completed">
            <p className="text-xs uppercase tracking-wide text-emerald-700">Completed</p>
            <p className="text-2xl font-bold text-emerald-900 flex items-center gap-1"><ClipboardCheck className="w-5 h-5" /> {completed}</p>
          </div>
          <div className="col-span-2 flex items-end gap-2 justify-end">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-44" data-testid="assessment-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All visit types</SelectItem>
                {ASSESSMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36" data-testid="assessment-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <AssessmentList items={items} canEdit={canEdit} showClient onOpen={(a) => setOpenId(a.id)} onChanged={load}
          emptyText="No assessments match. Start one with the button above." />

        <StartAssessmentDialog open={starting} onClose={() => setStarting(false)} clientId={startCtx.clientId} clientName={startCtx.clientName} defaultType={startCtx.defaultType || "admission"} onStarted={(a) => { reload(); setOpenId(a.id); }} />
        {openId && <AssessmentFiller assessmentId={openId} onClose={closeFiller} onCompleted={reload} />}
      </div>
    </Layout>
  );
}
